import { useEffect, useMemo, useState } from "react";

import deepmerge from "deepmerge";
import Highcharts from "highcharts/highmaps";
import { IntlShape, useIntl } from "react-intl";

import COMMON_HC_OPTIONS from "./dealsGeographyChartStaticOptions";
import fetchGeoDataForHcKey from "./fetchGeoDataForHcKey";
import getHcKeys from "./getDealHcKeys";

type DealsGroupedMap = Record<string, number>;

const prepareSeries = (
  raw: Highcharts.GeoJSON,
  dataSource: DealsGroupedMap,
  withDrillDown: boolean
): Highcharts.SeriesMapDataOptions[] =>
  Highcharts.geojson(raw).map((entry) => {
    const hcKey = entry.properties["hc-key"];
    const value = dataSource[hcKey] ?? 0;
    return { ...entry, value, ...(withDrillDown ? { drilldown: hcKey } : {}) };
  });

function createDrilldownHandler(
  dealsGrouped: DealsGroupedMap,
  intl: IntlShape
): Highcharts.DrilldownCallbackFunction {
  return async function (event) {
    const hcKey = event.point.options.drilldown;

    if (!hcKey) {
      return;
    }

    const [countryHCKeyPart, stateHCKeyPart] = hcKey.split("-");
    try {
      const geoJSON: Highcharts.GeoJSON = await fetchGeoDataForHcKey(
        `countries/${countryHCKeyPart}/${hcKey}-all`
      );

      this.addSeriesAsDrilldown(event.point, {
        data: prepareSeries(geoJSON, dealsGrouped, stateHCKeyPart === undefined),
        dataLabels: {
          enabled: true,
          format: "{point.properties.postal-code}",
        },
        name:
          // we need not official _levelNumber field to detect drilldown level
          // @ts-ignore
          event.target.series[0].options._levelNumber === 1
            ? intl.formatMessage({
                id: "reports.deals.geoChart.stateMapName",
                defaultMessage: "State Map",
                description: "State map name on geo chart at deals report page",
              })
            : intl.formatMessage({
                id: "reports.deals.geoChart.countryMapName",
                defaultMessage: "Country Map",
                description: "Country map name on geo chart at deals report page",
              }),
        type: "map",
      });
      this.setTitle({ text: "" }, { text: event.point.name });
    } catch {
      // do nothing
    }
  };
}

function createDrillupHandler(): Highcharts.DrillupCallbackFunction {
  return function () {
    this.setTitle({ text: "" }, { text: "" });
  };
}

const useGeoChartOptions = (dealsCountByHcKey: Record<string, number>) => {
  const intl = useIntl();
  const [rawGeoData, setRawGeoData] = useState<Highcharts.GeoJSON | undefined>(undefined);
  const dealsGrouped = Object.keys(dealsCountByHcKey).reduce(
    (subResult: Record<string, number>, hcKey) => {
      return getHcKeys(hcKey).reduce(
        (result, key) => ({ ...result, [key]: (result[key] ?? 0) + dealsCountByHcKey[hcKey] }),
        subResult
      );
    },
    {}
  );

  useEffect(() => {
    fetchGeoDataForHcKey("custom/world-eckert3-highres").then(setRawGeoData);
  }, []);

  const mapOptions = useMemo<Highcharts.Options | undefined>(() => {
    if (!rawGeoData) {
      return;
    }

    return deepmerge.all<Highcharts.Options>([
      {},
      COMMON_HC_OPTIONS,
      {
        chart: {
          events: {
            drilldown: createDrilldownHandler(dealsGrouped, intl),
            drillup: createDrillupHandler(),
          },
        },
        series: [
          {
            data: prepareSeries(rawGeoData, dealsGrouped, true),
            dataLabels: {
              enabled: true,
              format: "{point.properties.postal-code}",
            },
            name: intl.formatMessage({
              id: "reports.deals.geoChart.worldMapName",
              defaultMessage: "World Map",
              description: "World Map name on geo chart at deals report page",
            }),
            type: "map",
          },
        ],
      },
    ]);
  }, [dealsGrouped, rawGeoData, intl]);

  return mapOptions;
};

export default useGeoChartOptions;
